import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtDT, fmtDate } from '../../utils/helpers';
import { Spinner, EmptyState } from '../../components/common';
import toast from 'react-hot-toast';

// ── AI Dashboard ──────────────────────────────────────────────────────────────
export function AIDashboard() {
  const [data, setData]     = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, fc] = await Promise.all([
        api.get('/ai/dashboard').then(r=>r.data),
        api.get('/ai/demand-forecast?days=14').then(r=>r.data),
      ]);
      if (dash.success) setData(dash.data);
      if (fc.success)   setForecast(fc.data.forecasts||[]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runFraudScan = async () => {
    try {
      toast.loading('Running fraud scan…', { id:'fraud' });
      const r = await api.post('/ai/fraud-scan', {}).then(r=>r.data);
      toast.success(`Scan complete — ${r.data.alerts_generated} alerts generated`, { id:'fraud' });
      load();
    } catch { toast.error('Scan failed', { id:'fraud' }); }
  };

  const fraudBySeverity = data?.fraud?.by_severity || [];
  const criticalCount = fraudBySeverity.find(f=>f.severity==='CRITICAL')?.count || 0;
  const highCount     = fraudBySeverity.find(f=>f.severity==='HIGH')?.count || 0;

  return (
    <AppShell title="AI & Analytics Engine" actions={
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
        <button className="btn btn-primary btn-sm" onClick={runFraudScan}>🔍 Run Fraud Scan</button>
      </div>
    }>
      {loading && !data ? <Spinner /> : (
        <>
          {/* KPI cards */}
          <div className="stat-grid" style={{ marginBottom:24 }}>
            <AIKpi label="Open Fraud Alerts" value={fraudBySeverity.reduce((a,f)=>a+parseInt(f.count),0)}
              color={(criticalCount>0||highCount>0) ? 'var(--danger)' : 'var(--success)'}
              sub={criticalCount > 0 ? `${criticalCount} CRITICAL` : highCount > 0 ? `${highCount} HIGH` : 'All clear'} />
            <AIKpi label="Weekly Volume"    value={data?.weekly_volume||0} color="var(--navy)" sub="Last 7 days" />
            <AIKpi label="EDD Predictions"  value={data?.edd?.predictions_made||0} color="var(--info)" sub="Last 30 days" />
            <AIKpi label="Route Plans Today" value={data?.route_plans_today||0} color="var(--amber)" sub="Optimized routes" />
          </div>

          {/* Demand forecast chart */}
          <div className="card" style={{ marginBottom:20 }}>
            <div className="card-header">
              <span className="card-title">14-Day Demand Forecast</span>
              <span style={{ fontSize:'.75rem', color:'var(--gray-400)' }}>DOW-weighted trend model</span>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={forecast} margin={{ top:4, right:8, bottom:0, left:0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize:10 }} tickFormatter={d => d?.slice(5)} />
                  <YAxis tick={{ fontSize:10 }} />
                  <Tooltip formatter={(v,n) => [v, 'Predicted volume']} labelFormatter={l => fmtDate(l)} />
                  <Bar dataKey="predicted_volume" name="Predicted" fill="#1B2A4A" radius={[3,3,0,0]}
                    label={({ x, y, width, value, index }) =>
                      forecast[index]?.alert ? <text x={x+width/2} y={y-4} textAnchor="middle" fontSize={9} fill="var(--danger)">⚠</text> : null
                    }
                  />
                </BarChart>
              </ResponsiveContainer>
              {forecast.filter(f=>f.alert).length > 0 && (
                <div className="alert alert-warning" style={{ marginTop:12, padding:'8px 12px', fontSize:'.8rem' }}>
                  ⚠️ High demand forecast on: {forecast.filter(f=>f.alert).map(f=>f.date).join(', ')} — consider increasing rider capacity.
                </div>
              )}
            </div>
          </div>

          {/* Fraud alerts summary */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div className="card">
              <div className="card-header">
                <span className="card-title">Open Fraud Alerts by Severity</span>
              </div>
              <div className="card-body">
                {fraudBySeverity.length ? fraudBySeverity.map(f=>(
                  <div key={f.severity} className="flex-between" style={{ padding:'8px 0', borderBottom:'1px solid var(--gray-100)' }}>
                    <span className={`badge ${f.severity==='CRITICAL'?'badge-danger':f.severity==='HIGH'?'badge-warning':f.severity==='MEDIUM'?'badge-info':'badge-gray'}`}>
                      {f.severity}
                    </span>
                    <strong style={{ fontSize:'1.1rem' }}>{f.count}</strong>
                  </div>
                )) : <div style={{ textAlign:'center', color:'var(--success)', padding:24 }}>✅ No open fraud alerts</div>}
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">AI Module Status</span></div>
              <div className="card-body">
                {[
                  { name:'Route Optimisation',   status:'ACTIVE',    desc:'Nearest-neighbour + priority' },
                  { name:'EDD Prediction',        status:'ACTIVE',    desc:'DOW-weighted + load factor' },
                  { name:'Fraud Detection',       status:'ACTIVE',    desc:'Pattern-based rules engine' },
                  { name:'Demand Forecasting',    status:'ACTIVE',    desc:'Historical trend model' },
                  { name:'ML Route Learning',     status:'PHASE4',    desc:'Reinforcement learning' },
                  { name:'NLP Chatbot',           status:'PHASE4',    desc:'GPT-based intent resolution' },
                ].map(m=>(
                  <div key={m.name} className="flex-between" style={{ padding:'7px 0', borderBottom:'1px solid var(--gray-100)' }}>
                    <div>
                      <div style={{ fontSize:'.85rem', fontWeight:500 }}>{m.name}</div>
                      <div style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{m.desc}</div>
                    </div>
                    <span className={`badge ${m.status==='ACTIVE'?'badge-success':'badge-gray'}`}>{m.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}

// ── Route Optimiser ───────────────────────────────────────────────────────────
export function RouteOptimiserPage() {
  const [plan, setPlan]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [date, setDate]     = useState(new Date().toISOString().slice(0,10));
  const [riderId, setRiderId] = useState('');

  const optimise = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date });
      if (riderId) params.append('riderId', riderId);
      const r = await api.get(`/ai/route-plan?${params}`).then(r=>r.data);
      if (r.success) setPlan(r.data);
      else toast.error(r.message);
    } catch { toast.error('Failed to optimise route'); }
    finally { setLoading(false); }
  };

  return (
    <AppShell title="Route Optimisation Engine">
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-body">
          <div style={{ display:'flex', gap:12, alignItems:'flex-end' }}>
            <div className="form-group" style={{ margin:0 }}>
              <label className="form-label">Date</label>
              <input className="form-control" type="date" value={date} onChange={e=>setDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin:0, flex:1 }}>
              <label className="form-label">Rider ID (optional — leave blank for all)</label>
              <input className="form-control" placeholder="UUID of rider employee" value={riderId} onChange={e=>setRiderId(e.target.value)} />
            </div>
            <button className="btn btn-primary" onClick={optimise} disabled={loading}>
              {loading ? 'Optimising…' : '⚡ Optimise Route'}
            </button>
          </div>
        </div>
      </div>

      {plan && (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', marginBottom:20 }}>
            <AIKpi label="Total Stops"    value={plan.total_stops}                   color="var(--navy)" />
            <AIKpi label="Total Distance" value={`${plan.total_distance_km} km`}     color="var(--info)" />
            <AIKpi label="Est. Duration"  value={`${Math.floor(plan.estimated_time_minutes/60)}h ${plan.estimated_time_minutes%60}m`} color="var(--amber)" />
            <AIKpi label="Algorithm"      value="Nearest Neighbour"                  color="var(--success)" />
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Optimised Delivery Sequence</span>
              <button className="btn btn-outline btn-sm" onClick={() => {
                const content = plan.stops.map(s =>
                  `${s.sequence}. ${s.tracking_id} | ${s.customer_name} | ${s.line1}, ${s.city} | ${s.service_type} | COD:${s.cod_amount||0} | ~${s.estimated_distance_km}km`
                ).join('\n');
                const w = window.open('','_blank');
                w.document.write(`<pre style="font-family:monospace;font-size:11px;padding:20px">ROUTE PLAN — ${date}\nTotal: ${plan.total_stops} stops | ${plan.total_distance_km}km | ~${plan.estimated_time_minutes}min\n\n${content}</pre>`);
                w.print();
              }}>🖨️ Print Route Card</button>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Tracking ID</th><th>Customer</th><th>Address</th><th>Service</th><th>COD</th><th>Distance</th></tr></thead>
                <tbody>
                  {plan.stops.map(s=>(
                    <tr key={s.tracking_id} style={{ background: ['DOM_EXP','DOM_SD','INT_EXP'].includes(s.service_type) ? '#FFFBEB' : undefined }}>
                      <td style={{ fontWeight:700, color:'var(--navy)' }}>{s.sequence}</td>
                      <td><code style={{ fontSize:'.75rem' }}>{s.tracking_id}</code></td>
                      <td>
                        <div style={{ fontWeight:500 }}>{s.customer_name}</div>
                        <div style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{s.customer_mobile}</div>
                      </td>
                      <td style={{ fontSize:'.82rem' }}>{s.line1}, {s.city}</td>
                      <td>
                        <span className={`badge ${['DOM_EXP','DOM_SD'].includes(s.service_type)?'badge-warning':'badge-info'}`} style={{ fontSize:'.68rem' }}>
                          {s.service_type}
                        </span>
                      </td>
                      <td style={{ fontWeight:500 }}>{s.cod_amount > 0 ? `PKR ${s.cod_amount}` : '—'}</td>
                      <td style={{ fontSize:'.8rem', color:'var(--gray-500)' }}>{s.estimated_distance_km} km</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!plan.stops.length && <EmptyState icon="🗺️" title="No deliveries found for this date/rider" />}
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}

// ── Fraud Alerts ──────────────────────────────────────────────────────────────
export function FraudAlertsPage() {
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState('');
  const [status, setStatus]   = useState('OPEN');
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (severity) params.append('severity', severity);
      if (status)   params.append('status',   status);
      const r = await api.get(`/ai/fraud-alerts?${params}`).then(r=>r.data);
      if (r.success) setAlerts(r.data);
    } finally { setLoading(false); }
  }, [severity, status]);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.post('/ai/fraud-scan', {}).then(r=>r.data);
      toast.success(`${r.data.alerts_generated} new alerts generated`);
      load();
    } catch { toast.error('Scan failed'); }
    finally { setScanning(false); }
  };

  const resolve = async (alertId, newStatus) => {
    try {
      await api.patch(`/ai/fraud-alerts/${alertId}`, { status:newStatus });
      toast.success('Alert updated'); load();
    } catch { toast.error('Failed'); }
  };

  const SEV_COLORS = { CRITICAL:'badge-danger', HIGH:'badge-warning', MEDIUM:'badge-info', LOW:'badge-gray' };
  const TYPE_ICONS = { COD_REPEAT_REFUSAL:'🔄', WEIGHT_MANIPULATION:'⚖️', HIGH_RTO_RATE:'↩️', UNUSUAL_PAYMENT_PATTERN:'💳', ROUTE_DEVIATION:'📍' };

  return (
    <AppShell title="Fraud & Anomaly Detection" actions={
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-primary btn-sm" onClick={runScan} disabled={scanning}>
          {scanning ? 'Scanning…' : '🔍 Run Scan Now'}
        </button>
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>
    }>
      <div className="filters-bar">
        <select className="form-control" style={{ width:140 }} value={severity} onChange={e=>setSeverity(e.target.value)}>
          <option value="">All Severities</option>
          {['CRITICAL','HIGH','MEDIUM','LOW'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-control" style={{ width:160 }} value={status} onChange={e=>setStatus(e.target.value)}>
          {['OPEN','INVESTIGATING','RESOLVED','FALSE_POSITIVE'].map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : alerts.length ? (
            <table>
              <thead><tr><th>Type</th><th>Description</th><th>Severity</th><th>Status</th><th>Detected</th><th>Actions</th></tr></thead>
              <tbody>
                {alerts.map(a=>(
                  <tr key={a.alert_id} style={{ background: a.severity==='CRITICAL' ? '#FEF2F2' : a.severity==='HIGH' ? '#FFFBEB' : undefined }}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span style={{ fontSize:'1.1rem' }}>{TYPE_ICONS[a.alert_type]||'⚠️'}</span>
                        <span style={{ fontSize:'.78rem', fontWeight:600 }}>{a.alert_type.replace(/_/g,' ')}</span>
                      </div>
                      {a.tracking_id && <code style={{ fontSize:'.7rem', color:'var(--navy)' }}>{a.tracking_id}</code>}
                    </td>
                    <td style={{ maxWidth:280 }}>
                      <span style={{ fontSize:'.82rem' }}>{a.description}</span>
                    </td>
                    <td><span className={`badge ${SEV_COLORS[a.severity]||'badge-gray'}`}>{a.severity}</span></td>
                    <td><span className={`badge ${a.status==='OPEN'?'badge-danger':a.status==='RESOLVED'?'badge-success':a.status==='FALSE_POSITIVE'?'badge-gray':'badge-warning'}`}>{a.status}</span></td>
                    <td className="text-sm">{fmtDT(a.created_at)}</td>
                    <td>
                      {a.status === 'OPEN' && (
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-outline btn-sm" style={{ fontSize:'.68rem' }} onClick={()=>resolve(a.alert_id,'INVESTIGATING')}>Investigate</button>
                          <button className="btn btn-success btn-sm"  style={{ fontSize:'.68rem' }} onClick={()=>resolve(a.alert_id,'RESOLVED')}>Resolve</button>
                          <button className="btn btn-outline btn-sm"  style={{ fontSize:'.68rem' }} onClick={()=>resolve(a.alert_id,'FALSE_POSITIVE')}>FP</button>
                        </div>
                      )}
                      {a.status==='INVESTIGATING' && (
                        <button className="btn btn-success btn-sm" onClick={()=>resolve(a.alert_id,'RESOLVED')}>Mark Resolved</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign:'center', padding:48 }}>
              <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
              <div style={{ fontWeight:600, color:'var(--success)' }}>No {status.toLowerCase()} alerts</div>
              <div style={{ color:'var(--gray-400)', fontSize:'.85rem', marginTop:4 }}>Run a scan to detect new anomalies</div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

// ── EDD Predictor tool ────────────────────────────────────────────────────────
export function EDDPredictorPage() {
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ serviceType:'DOM_STD', weight:'1', bookingTime:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const predict = async () => {
    setLoading(true);
    try {
      const r = await api.post('/ai/predict-edd', {
        serviceType: form.serviceType,
        weight: parseFloat(form.weight),
        bookingTime: form.bookingTime || new Date().toISOString(),
      }).then(r=>r.data);
      if (r.success) setResult(r.data);
    } catch { toast.error('Prediction failed'); }
    finally { setLoading(false); }
  };

  const SERVICES = ['DOM_STD','DOM_EXP','DOM_SD','DOM_ECO','INT_AIR','INT_SEA','INT_EXP','CARGO_BULK'];

  return (
    <AppShell title="EDD Prediction Engine">
      <div style={{ maxWidth:600, margin:'0 auto', display:'grid', gap:16 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Predict Delivery Date</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Service Type</label>
                <select className="form-control" value={form.serviceType} onChange={set('serviceType')}>
                  {SERVICES.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Package Weight (kg)</label>
                <input className="form-control" type="number" step="0.1" min="0.1" value={form.weight} onChange={set('weight')} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Booking Time <span style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>(leave blank for now)</span></label>
              <input className="form-control" type="datetime-local" value={form.bookingTime} onChange={set('bookingTime')} />
            </div>
            <button className="btn btn-primary w-full" onClick={predict} disabled={loading}>
              {loading ? 'Predicting…' : '🤖 Predict EDD'}
            </button>
          </div>
        </div>

        {result && (
          <div className="card" style={{ borderLeft:'4px solid var(--amber)' }}>
            <div className="card-body">
              <div style={{ textAlign:'center', padding:'8px 0 20px' }}>
                <div style={{ fontSize:'.8rem', color:'var(--gray-400)', textTransform:'uppercase', letterSpacing:'.1em' }}>Predicted Delivery Date</div>
                <div style={{ fontSize:'2rem', fontWeight:700, color:'var(--navy)', margin:'8px 0' }}>{result.predicted_date}</div>
                <div style={{ fontSize:'.9rem', color:'var(--gray-500)' }}>Range: {result.range}</div>
                <div style={{ marginTop:12 }}>
                  <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'var(--gray-50)', padding:'6px 16px', borderRadius:999 }}>
                    <div style={{ width:12, height:12, borderRadius:'50%',
                      background: result.confidence_pct >= 85 ? 'var(--success)' : result.confidence_pct >= 70 ? 'var(--warning)' : 'var(--danger)' }} />
                    <span style={{ fontSize:'.85rem', fontWeight:600 }}>{result.confidence_pct}% confidence</span>
                  </div>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <PredRow label="Predicted days"   v={result.predicted_days} />
                <PredRow label="Base TAT"         v={`${result.base_tat} days`} />
                <PredRow label="Load factor"      v={`×${result.load_factor}`} />
                <PredRow label="Sample size"      v={`${result.sample_size} shipments`} />
                <PredRow label="Model"            v={result.model_version} />
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function AIKpi({ label, value, color, sub }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      {sub && <div style={{ fontSize:'.72rem', color:'var(--gray-400)', marginTop:4 }}>{sub}</div>}
    </div>
  );
}
function PredRow({ label, v }) {
  return (
    <div style={{ padding:'6px 0', borderBottom:'1px solid var(--gray-100)' }}>
      <div style={{ fontSize:'.7rem', color:'var(--gray-400)' }}>{label}</div>
      <div style={{ fontSize:'.85rem', fontWeight:600 }}>{v}</div>
    </div>
  );
}

// ── Hub Capacity Alert Page (AI-FE-004) ───────────────────────────────────────
export function HubCapacityPage() {
  const [hubs, setHubs]   = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/ai/hub-capacity').then(r=>r.data);
      if (r.success) { setHubs(r.data); setAlerts(r.alerts||[]); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pct = (v) => parseFloat(v||0);

  return (
    <AppShell title="Hub Capacity — 72h Forecast" actions={<button className="btn btn-outline btn-sm" onClick={load}>↻</button>}>
      {alerts.length > 0 && (
        <div className="alert alert-warning" style={{ marginBottom:16, display:'flex', gap:10 }}>
          <span style={{ fontSize:'1.3rem' }}>⚠️</span>
          <div>
            <strong>Capacity Alert:</strong> {alerts.length} hub{alerts.length>1?'s':''} forecast above 80% in the next 72 hours.
            <div style={{ fontSize:'.82rem', marginTop:4 }}>{alerts.map(a=>`${a.name} (${a.forecast_utilisation_pct}%)`).join(', ')}</div>
          </div>
        </div>
      )}
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : hubs.length ? (
            <table>
              <thead>
                <tr><th>Hub</th><th>Type</th><th>Capacity</th><th>Avg Daily</th><th>72h Forecast</th><th>Utilisation</th><th>Status</th></tr>
              </thead>
              <tbody>
                {hubs.map(h => {
                  const util = pct(h.forecast_utilisation_pct);
                  const color = util >= 90 ? 'var(--danger)' : util >= 80 ? 'var(--warning)' : 'var(--success)';
                  return (
                    <tr key={h.hub_id} style={{ background: util >= 90 ? '#FEF2F2' : util >= 80 ? '#FFFBEB' : undefined }}>
                      <td><strong>{h.name}</strong><div style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{h.code}</div></td>
                      <td><span className="badge badge-navy" style={{ fontSize:'.65rem' }}>{h.type||'HUB'}</span></td>
                      <td style={{ fontWeight:500 }}>{Number(h.capacity).toLocaleString()}</td>
                      <td>{Math.round(h.avg_daily_volume)}</td>
                      <td style={{ fontWeight:600 }}>{Math.round(h.forecast_72h)}</td>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ flex:1, height:8, background:'var(--gray-200)', borderRadius:4, overflow:'hidden' }}>
                            <div style={{ width:`${Math.min(util,100)}%`, height:'100%', background:color, borderRadius:4, transition:'width .4s' }}/>
                          </div>
                          <span style={{ fontWeight:700, color, fontSize:'.85rem', minWidth:40 }}>{util.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td>
                        {util >= 90 ? <span className="badge badge-danger">CRITICAL</span>
                        : util >= 80 ? <span className="badge badge-warning">ALERT</span>
                        : <span className="badge badge-success">OK</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : <EmptyState icon="🏢" title="No hub data available" />}
        </div>
      </div>
    </AppShell>
  );
}
